import React from "react"
import styled from "styled-components"
import {Container, Grid, Row, Col} from "@uprise/grid"
import {TextInputHorizontal} from "@uprise/form"
import {H5, H6} from "@uprise/headings"
import {P} from "@uprise/text"
import {Button} from "@uprise/button"
import moment from "moment"
import {useForm, Controller} from "react-hook-form"

import DropDown from "components/shared/DropDown"
import Datepicker from "components/shared/DatePicker"

const ElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid #edeafa;
`

const ElementLabel = styled(H6)`
	margin: 16px 0 8px 0;
	width: 150px;
	max-width: 150px;
`

const ItemValueContainer = styled.div`
	/* flex-basis: 60%; */
	color: #6d6c79;
	min-width: 100px;
	text-align: center;
	align-self: center;
`

const ButtonContainer = styled.div`
	display: flex;
`

const NavigationButton = styled(Button)`
	width: 100px;
	height: 50px;
	margin: 0 6px;
`

const Plan = ({data, isEditEnabled, onEditCancel}) => {
	const {register, control, handleSubmit, watch, reset, trigger, setError, errors} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})

	const onSubmit = () => {}

	return (
		<Container data-testid='planAndAddOnsData'>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Row>
					<Col className='col-12 col-lg-12 col-md-6 col-sm-12'>
						<H5>Plan Details</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<ElementContainer>
							<ElementLabel>Plan Status</ElementLabel>
							<ItemValueContainer>Active</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Pricing Type</ElementLabel>

							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										name={"pricingType"}
										control={control}
										defaultValue={""}
										rules={{required: true}}
										render={props => (
											<DropDown
												variant='primary'
												items={[
													{
														label: "Variable Fee",
														value: "variable"
													},
													{
														label: "Fixed Annual Fee",
														value: "fixed"
													}
												]}
												label='Pricing Type'
												onChange={item => props.onChange(item.value)}
												initialSelectedItem={""}
												menuStyle={{position: "absolute", zIndex: 2}}
											/>
										)}
									/>
								) : (
									<P>{data.plan.pricingType}</P>
								)}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Plan Start</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										name={"dateSigned"}
										control={control}
										rules={{required: true}}
										defaultValue={moment(data?.contract?.dateSigned)}
										render={props => {
											return (
												<Datepicker
													date={props.value}
													onChange={e => props.onChange(e.target.value)}
												/>
											)
										}}
									/>
								) : (
									<P>
										{data?.contract?.dateSigned
											? moment(data?.contract?.dateSigned).format("Do MMM YYYY")
											: "-"}
									</P>
								)}
							</ItemValueContainer>
						</ElementContainer>
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<ElementContainer>
							<ElementLabel>Employees on Plan</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										name={"maxUser"}
										control={control}
										defaultValue={data.code.maxUser}
										as={<TextInputHorizontal id='maxUsers' type='number' placeholder='Max users' />}
									/>
								) : (
									<P>{data.code.maxUser}</P>
								)}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Price per Employee</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										control={control}
										name={"price"}
										defaultValue={data.plan.price}
										as={<TextInputHorizontal id='price' type='number' placeholder='' />}
									/>
								) : (
									<P>{data.plan.price} AUD</P>
								)}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Plan End</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										name={"expiryDate"}
										control={control}
										defaultValue={moment(data?.contract?.expiryDate)}
										render={props => {
											return (
												<Datepicker
													date={props.value}
													onChange={e => props.onChange(e.target.value)}
												/>
											)
										}}
									/>
								) : (
									<P>
										{data?.contract?.expiryDate
											? moment(data?.contract?.expiryDate).format("Do MMM YYYY")
											: "-"}
									</P>
								)}
							</ItemValueContainer>
						</ElementContainer>
					</Col>
				</Row>

				<Row>
					<Col className='col-12 col-lg-12 col-md-6 col-sm-12'>
						<H5>Plan Features</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						{data.plan.featurePricing &&
							data.plan.featurePricing.slice(0, 6).map((item, index) => {
								return (
									<ElementContainer key={index}>
										<ElementLabel>{item.label}</ElementLabel>
										<ItemValueContainer>
											{item.enabled ? "Included" : "Excluded"}
										</ItemValueContainer>
										<ItemValueContainer>
											{item.enabled ? `${item.fee} per ${item.per}` : "-"}
										</ItemValueContainer>
									</ElementContainer>
								)
							})}
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						{data.plan.featurePricing &&
							data.plan.featurePricing.slice(6).map((item, index) => {
								return (
									<ElementContainer key={index}>
										<ElementLabel>{item.label}</ElementLabel>
										<ItemValueContainer>
											{item.enabled ? "Included" : "Excluded"}
										</ItemValueContainer>
										<ItemValueContainer>
											{!["additional-sessions", "care-navigator"].includes(item.id) && item.enabled
												? `${item.fee} per ${item.per}`
												: "-"}
										</ItemValueContainer>
									</ElementContainer>
								)
							})}
					</Col>
				</Row>

				<Row>
					<Col className='col-12 col-lg-12 col-md-6 col-sm-12'>
						<H5>Add-On(s)</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<ElementContainer>
							<ElementLabel>Add-On Type</ElementLabel>
							<ItemValueContainer>
								{data?.plan?.addOnType?.value ? data.plan.addOnType.value : data.plan.addOnType}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>No.of Hours Purchased</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										control={control}
										name={"prePurchased"}
										defaultValue={data.plan.prePurchased}
										as={<TextInputHorizontal id='prePurchased' type='number' placeholder='' />}
									/>
								) : (
									<P>{data.plan.prePurchased}</P>
								)}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>No.of Hours Used</ElementLabel>
							<ItemValueContainer>{data.plan.pricingType}</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Balance Remaining</ElementLabel>
							<ItemValueContainer>{data.plan.pricingType}</ItemValueContainer>
						</ElementContainer>
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<ElementContainer>
							<ElementLabel>Price per Hour</ElementLabel>
							<ItemValueContainer>
								{isEditEnabled ? (
									<Controller
										defaultValue={data.plan.prePurchasedPrice}
										control={control}
										name={"prePurchasedPrice"}
										as={<TextInputHorizontal id='price' type='number' placeholder='' />}
									/>
								) : (
									<P>{data.plan.prePurchasedPrice}</P>
								)}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Date of Purchase</ElementLabel>
							<ItemValueContainer>
								{moment(data?.contract?.dateSigned).format("Do MMM YYYY")}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Date of Expiry</ElementLabel>
							<ItemValueContainer>
								{moment(data?.contract?.expiryDate).format("Do MMM YYYY")}
							</ItemValueContainer>
						</ElementContainer>

						<ElementContainer>
							<ElementLabel>Total Cost</ElementLabel>
							<ItemValueContainer>{data?.plan?.pricingType}</ItemValueContainer>
						</ElementContainer>
					</Col>
				</Row>
				{isEditEnabled ? (
					<Row className='flex-row-reverse'>
						<ButtonContainer>
							<NavigationButton type='submit' variant={"primary"} title={"Update"} />
						</ButtonContainer>
						<ButtonContainer>
							<NavigationButton variant={"secondary"} onClick={onEditCancel} title={"Cancel"} />
						</ButtonContainer>
					</Row>
				) : null}
			</form>
		</Container>
	)
}

export default Plan
